import React, { useEffect, useState } from 'react';
import { RefreshCw, Scale } from 'lucide-react';
import { toast } from 'sonner';
import { apiCall } from '../../utils/api';

/** Admin queue for disputed cancellation payment claims. */
export default function PaymentDisputes() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [decision, setDecision] = useState('approve_full');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await apiCall('/api/admin/payment-claims?status=disputed');
      setClaims(res.claims || []);
    } catch (_e) { /* silent */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resolve = async () => {
    if (!note.trim()) { toast.error('A resolution note is required'); return; }
    if (decision === 'approve_partial' && (!amount || parseFloat(amount) <= 0)) {
      toast.error('Enter the partial amount'); return;
    }
    setBusy(true);
    try {
      const body = { decision, note: note.trim() };
      if (decision === 'approve_partial') body.amount = parseFloat(amount);
      const res = await apiCall(`/api/admin/payment-claims/${target._id}/resolve`, {
        method: 'POST', body: JSON.stringify(body),
      });
      toast.success(`Dispute resolved: ${res.status.replace(/_/g, ' ')}`);
      setTarget(null); setNote(''); setAmount('');
      load();
    } catch (e) { toast.error(e.message || 'Failed'); } finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-4" data-testid="payment-disputes-panel">
      <div className="flex items-center gap-2">
        <Scale className="w-5 h-5 text-purple-600" />
        <h3 className="text-lg font-bold text-slate-800">Payment Disputes</h3>
        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">{claims.length}</span>
      </div>
      {claims.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
          No open disputes. Disputed cancellation payment claims will appear here for final resolution.
        </div>
      )}
      {claims.map(c => (
        <div key={c._id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`dispute-${c._id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="font-semibold text-slate-800 truncate max-w-lg" title={c.job_title}>{c.job_title}</p>
              <p className="text-sm text-slate-500">
                {c.worker_name} • claimed <span className="capitalize">{c.completion_level?.replace('_', ' ')}</span> • requested ${c.amount_requested} of ${c.job_value}
                {c.amount_approved != null && ` • employer approved $${c.amount_approved}`}
              </p>
              {c.decision_note && <p className="text-xs text-slate-500 mt-1">Employer note: "{c.decision_note}"</p>}
              {c.dispute_reason && <p className="text-xs text-purple-700 mt-1">Worker dispute: "{c.dispute_reason}"</p>}
              <p className="text-xs text-slate-400 mt-1">
                Evidence: {c.evidence?.has_checkin ? `GPS check-in ✓${c.evidence.gps_verified_hours != null ? ` (${c.evidence.gps_verified_hours}h)` : ''}` : 'no GPS check-in'}
                {c.evidence?.tasks_total != null && ` • ${c.evidence.tasks_completed}/${c.evidence.tasks_total} tasks`}
              </p>
            </div>
            <button
              onClick={() => { setTarget(c); setDecision('approve_full'); setAmount(''); setNote(''); }}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 shrink-0"
              data-testid={`resolve-dispute-${c._id}`}
            >
              Resolve
            </button>
          </div>
        </div>
      ))}

      {target && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-800">Resolve Dispute</h3>
              <p className="text-sm text-slate-500 mt-1 truncate">{target.job_title} — {target.worker_name}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Final decision *</label>
                <select value={decision} onChange={(e) => setDecision(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500"
                  data-testid="dispute-decision-select">
                  <option value="approve_full">Approve full payment (${target.amount_requested})</option>
                  <option value="approve_partial">Approve partial payment…</option>
                  <option value="uphold_rejection">Uphold rejection (no payment)</option>
                </select>
              </div>
              {decision === 'approve_partial' && (
                <input type="number" min="0.01" step="0.01" max={target.job_value}
                  value={amount} onChange={(e) => setAmount(e.target.value)}
                  placeholder={`Amount (max $${target.job_value})`}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500"
                  data-testid="dispute-amount-input" />
              )}
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                placeholder="Resolution rationale (visible to both parties + audit trail) *"
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 resize-none"
                data-testid="dispute-note-input" />
              <div className="flex justify-end gap-3">
                <button onClick={() => setTarget(null)} className="px-5 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl transition">Cancel</button>
                <button onClick={resolve} disabled={busy}
                  className="px-5 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition disabled:opacity-50"
                  data-testid="confirm-resolve-btn">
                  {busy ? 'Resolving…' : 'Resolve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
